import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { TableContainer, Table, TableCell, TableBody } from '@material-ui/core';
import { AutoSizer } from 'react-virtualized';
import { MultiGrid } from 'react-virtualized';
import { makeStyles } from '@material-ui/core/styles';
import { Popover } from '@material-ui/core';
import { useRef } from 'react';
import styles from './VirtualizedTable.module.css';
/*
 * Note: tableRowClassName will still be applied to each cell individually to mimic styling of a row.
 * Conditional row styles can be applied by passing a function for the same prop instead.
 */
const VirtualizedTable = ({
  classNames = {},
  listHeight,
  rows: data,
  columns: baseColumns,
  rowHeight,
  fixedColumnCount = 0,
  tableWidth = '100%',
  onScroll,
  scrollTop,
  enableAutoHeight = false,
  maxEmptyRowPadding = 10,
  rowColors = ['white', '#f1f1f2'],
  ...props
}) => {
  const header = {};
  const gridRef = useRef(null);
  const columns = [
    ...baseColumns,
    {
      title: '',
      field: '_dummy',
      isLastColumn: true,
      width: 0,
      prevColsWidth: baseColumns
        .map(col => col.width)
        .reduce((acc, curr) => acc + curr, 0),
    },
  ];
  columns.forEach(column => {
    header[column.field] = column.title;
  });

  const paddingRowsCount = Math.max(maxEmptyRowPadding - data.length, 0);
  const paddingRows = Array(paddingRowsCount).fill({ isPaddingRow: true });

  const rows = [header, ...data, ...paddingRows];

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popoverVal, setPopoverVal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event, val, maxLength) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setPopoverVal(val);
      setIsPopupOpen(true);
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = event => {
    setIsPopupOpen(false);
    setAnchorEl(null);
  };

  const closePopup = () => setIsPopupOpen(false);
  const usePopoverStyles = makeStyles(theme => ({
    popover: {
      pointerEvents: 'none',
    },
    paper: {
      padding: theme.spacing(1),
      color: 'white',
      backgroundColor: 'grey',
      fontSize: '13px', //TODO: keep same as table cell
    },
  }));

  const popoverClasses = usePopoverStyles();

  useEffect(() => {
    gridRef.current.recomputeGridSize();
  }, [baseColumns.length, data]);

  function cellRenderer({ columnIndex, key, rowIndex, style }) {
    const row = rows[rowIndex];
    const col = columns[columnIndex];

    const isHeader = rowIndex === 0;
    const contextualStyles = isHeader ? col.headerStyles : col.cellStyles;

    const renderColumnCell = () =>
      col.render && row ? col.render(row, rowIndex) : row[col.field];

    const renderHeaderCell = () =>
      col.renderHeader ? col.renderHeader(row) : row[col.field];

    const backgroundColor = rowIndex % 2 ? rowColors[1] : rowColors[0];
    if (!row) return <div style={style}></div>;
    return (
      <TableCell
        className={clsx(
          classNames.tableCellClassName,
          classNames.tableRowClassName instanceof Function
            ? classNames.tableRowClassName(row, rowIndex)
            : classNames.tableRowClassName,
          !isHeader && styles.virtualizedTableCell,
        )}
        style={{
          ...style,
          backgroundColor,
          ...col.style,
          ...contextualStyles,
        }}
        key={key}
        onMouseEnter={e =>
          !isHeader && handlePopoverOpen(e, row[col.field], col.maxLength)
        }
        onMouseLeave={e => !isHeader && handlePopoverClose(e)}
      >
        {row.isPaddingRow
          ? null
          : isHeader
          ? renderHeaderCell()
          : renderColumnCell()}
      </TableCell>
    );
  }

  return (
    <>
      <TableContainer
        style={{ width: tableWidth, height: '100%' }}
        className={classNames.tableContainerClassName}
      >
        <Table
          stickyHeader
          className={classNames.tableClassName}
          style={{ height: '100%' }}
        >
          <AutoSizer>
            {({ height, width: windowWidth }) => {
              let width = windowWidth;
              if (process.env.NODE_ENV === 'test') width = 2000; // to render in JSDOM
              return (
                <TableBody>
                  <MultiGrid
                    ref={gridRef}
                    cellRenderer={cellRenderer}
                    columnWidth={({ index }) => {
                      const col = columns[index];
                      if (col.isLastColumn) {
                        return Math.max(width - col.prevColsWidth, 0);
                      }
                      return col.width;
                    }}
                    columnCount={columns.length}
                    fixedColumnCount={
                      fixedColumnCount === baseColumns.length
                        ? fixedColumnCount + 1 //add dummy column into fixed columns to keep scroll behaviour same
                        : fixedColumnCount
                    }
                    fixedRowCount={1}
                    height={enableAutoHeight ? height : listHeight}
                    rowHeight={rowHeight}
                    rowCount={rows.length}
                    width={width}
                    enableFixedColumnScroll
                    hideBottomLeftGridScrollbar={true}
                    onScroll={onScroll}
                    scrollTop={scrollTop}
                    {...props}
                  />
                </TableBody>
              );
            }}
          </AutoSizer>
        </Table>
      </TableContainer>
      <Popover
        open={isPopupOpen}
        anchorEl={anchorEl}
        onClose={closePopup}
        className={popoverClasses.popover}
        classes={{
          paper: popoverClasses.paper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {popoverVal}
      </Popover>
    </>
  );
};

VirtualizedTable.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableHeadClassName: PropTypes.string,
    tableBodyClassName: PropTypes.string,
    tableCellClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  }),
  listHeight: PropTypes.number.isRequired,
  rows: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
      width: PropTypes.number.isRequired,
      style: PropTypes.object,
      headerStyles: PropTypes.object,
      cellStyles: PropTypes.object,
      render: PropTypes.func,
    }),
  ).isRequired,
  onScroll: PropTypes.func,
  scrollTop: PropTypes.number,
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  fixedColumnCount: PropTypes.number,
  enableAutoHeight: PropTypes.bool,
  maxEmptyRowPadding: PropTypes.number,
  rowColors: PropTypes.array,
};

export default VirtualizedTable;
